import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ChevronLeft, MessageSquarePlus, CheckCircle2, User } from "lucide-react";

type LessonRow = {
  slug: string;
  title: string;
  order_index: number;
  homework_intro: string | null;
  homework_questions: HomeworkQuestion[];
};

type HomeworkQuestion = {
  id: string;
  label: string;
  type: "long" | "short";
};

type SubmissionRow = {
  id: string;
  user_id: string;
  lesson_slug: string;
  responses: Record<string, string>;
  submitted_at: string;
  updated_at: string;
  feedback: string | null;
  feedback_at: string | null;
};

type ProfileRow = { id: string; email: string; full_name: string | null };
type LeadRow = { email: string; name: string | null; phone: string | null };

const HomeworkReviewSection = () => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [subs, setSubs] = useState<SubmissionRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileRow>>({});
  const [leadsByEmail, setLeadsByEmail] = useState<Record<string, LeadRow>>({});
  const [selectedLesson, setSelectedLesson] = useState<string | null>(null);
  const [viewing, setViewing] = useState<SubmissionRow | null>(null);
  const [feedbackDraft, setFeedbackDraft] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: lessonsData }, { data: subsData }, { data: profilesData }, { data: leadsData }] = await Promise.all([
      supabase
        .from("lessons")
        .select("slug, title, order_index, homework_intro, homework_questions")
        .eq("is_published", true)
        .order("order_index", { ascending: true }),
      supabase
        .from("homework_submissions")
        .select("id, user_id, lesson_slug, responses, submitted_at, updated_at, feedback, feedback_at")
        .order("submitted_at", { ascending: false }),
      supabase.from("profiles").select("id, email, full_name"),
      supabase.from("leads").select("email, name, phone"),
    ]);
    setLessons(
      ((lessonsData || []) as any[]).map((l) => ({
        ...l,
        homework_questions: (l.homework_questions as HomeworkQuestion[]) || [],
      })),
    );
    setSubs(((subsData || []) as any[]) as SubmissionRow[]);
    const pMap: Record<string, ProfileRow> = {};
    (profilesData || []).forEach((p: any) => (pMap[p.id] = p));
    setProfiles(pMap);
    const lMap: Record<string, LeadRow> = {};
    (leadsData || []).forEach((l: any) => (lMap[l.email.toLowerCase()] = l));
    setLeadsByEmail(lMap);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const subsByLesson = useMemo(() => {
    const map: Record<string, SubmissionRow[]> = {};
    subs.forEach((s) => {
      (map[s.lesson_slug] = map[s.lesson_slug] || []).push(s);
    });
    return map;
  }, [subs]);

  const displayName = (userId: string) => {
    const p = profiles[userId];
    if (!p) return { name: "Unknown student", email: "—", phone: "—" };
    const lead = leadsByEmail[p.email.toLowerCase()];
    return {
      name: p.full_name || lead?.name || p.email,
      email: p.email,
      phone: lead?.phone || "—",
    };
  };

  const openSubmission = (s: SubmissionRow) => {
    setViewing(s);
    setFeedbackDraft(s.feedback || "");
  };

  const saveFeedback = async () => {
    if (!viewing) return;
    setSavingFeedback(true);
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("homework_submissions")
      .update({
        feedback: feedbackDraft.trim() || null,
        feedback_at: feedbackDraft.trim() ? new Date().toISOString() : null,
        feedback_by: feedbackDraft.trim() ? userData?.user?.id ?? null : null,
      })
      .eq("id", viewing.id);
    setSavingFeedback(false);
    if (error) return toast.error(error.message);
    toast.success("Feedback saved — the student will see it in their portal.");
    setSubs((prev) =>
      prev.map((s) =>
        s.id === viewing.id
          ? { ...s, feedback: feedbackDraft.trim() || null, feedback_at: feedbackDraft.trim() ? new Date().toISOString() : null }
          : s,
      ),
    );
    setViewing(null);
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  // Detail view
  if (viewing) {
    const lesson = lessons.find((l) => l.slug === viewing.lesson_slug);
    const questions = lesson?.homework_questions || [];
    const info = displayName(viewing.user_id);
    return (
      <div className="space-y-6">
        <button
          onClick={() => setViewing(null)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm"
        >
          <ChevronLeft size={16} /> Back to submissions
        </button>

        <div className="border border-gold/15 bg-card p-6 md:p-8 space-y-2">
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-body">{lesson?.title || viewing.lesson_slug}</p>
          <h3 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <User size={20} className="text-gold" /> {info.name}
          </h3>
          <p className="font-body text-sm text-muted-foreground">
            {info.email} · {info.phone}
          </p>
          <p className="font-body text-xs text-muted-foreground">
            Submitted {formatDate(viewing.submitted_at)}
            {viewing.updated_at !== viewing.submitted_at && ` · Updated ${formatDate(viewing.updated_at)}`}
          </p>
        </div>

        <div className="space-y-5">
          {questions.map((q, idx) => (
            <div key={q.id} className="border border-gold/10 bg-card/60 p-5 space-y-2">
              <p className="font-body text-sm text-foreground">
                <span className="text-gold mr-2">{String(idx + 1).padStart(2, "0")}.</span>
                {q.label}
              </p>
              <p className="font-body text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {viewing.responses?.[q.id]?.trim() || <span className="italic">No response</span>}
              </p>
            </div>
          ))}
          {questions.length === 0 && (
            <p className="text-muted-foreground font-body text-sm">This lesson has no homework questions.</p>
          )}
        </div>

        <div className="border border-gold/30 bg-card p-6 md:p-8 space-y-4">
          <div className="flex items-center gap-2">
            <MessageSquarePlus size={18} className="text-gold" />
            <h4 className="font-display text-lg font-semibold text-foreground">Your Feedback</h4>
            {viewing.feedback_at && (
              <span className="text-xs text-muted-foreground font-body">Last saved {formatDate(viewing.feedback_at)}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-body">
            The student will see this on their lesson page next time they open it.
          </p>
          <textarea
            value={feedbackDraft}
            onChange={(e) => setFeedbackDraft(e.target.value)}
            rows={7}
            maxLength={5000}
            placeholder="Write personal feedback, encouragement, or next steps for this student..."
            className="w-full bg-background border border-gold/20 focus:border-gold/60 outline-none p-3 font-body text-sm text-foreground"
          />
          <div className="flex gap-3">
            <button
              onClick={saveFeedback}
              disabled={savingFeedback}
              className="px-5 py-2.5 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.2em] disabled:opacity-60"
            >
              {savingFeedback ? "Saving..." : "Save Feedback"}
            </button>
            {viewing.feedback && (
              <button
                onClick={() => {
                  setFeedbackDraft("");
                }}
                className="px-5 py-2.5 border border-gold/20 text-muted-foreground hover:text-foreground font-body text-xs uppercase tracking-[0.2em]"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Lesson list view
  if (!selectedLesson) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground font-body text-sm">
          Choose a lesson to review student homework submissions and leave written feedback.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          {lessons.map((l) => {
            const count = subsByLesson[l.slug]?.length || 0;
            const withFeedback = (subsByLesson[l.slug] || []).filter((s) => s.feedback).length;
            const hasQuestions = (l.homework_questions?.length || 0) > 0;
            return (
              <button
                key={l.slug}
                onClick={() => setSelectedLesson(l.slug)}
                disabled={!hasQuestions}
                className="text-left border border-gold/15 bg-card p-5 hover:border-gold/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <p className="text-xs uppercase tracking-[0.3em] text-gold font-body">
                  {l.order_index === 0 ? "Module" : `Lesson ${l.order_index}`}
                </p>
                <p className="font-display text-lg font-semibold text-foreground mt-1">{l.title}</p>
                <p className="font-body text-sm text-muted-foreground mt-2">
                  {hasQuestions ? `${count} submission${count === 1 ? "" : "s"} · ${withFeedback} reviewed` : "No homework"}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Submissions list for a lesson
  const lesson = lessons.find((l) => l.slug === selectedLesson);
  const list = subsByLesson[selectedLesson] || [];
  return (
    <div className="space-y-4">
      <button
        onClick={() => setSelectedLesson(null)}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm"
      >
        <ChevronLeft size={16} /> All lessons
      </button>
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-body">
          {lesson?.order_index === 0 ? "Module" : `Lesson ${lesson?.order_index}`}
        </p>
        <h3 className="font-display text-2xl font-bold text-foreground">{lesson?.title}</h3>
      </div>

      <div className="border border-gold/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gold/10 bg-secondary/30">
                <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Student</th>
                <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Email</th>
                <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Submitted</th>
                <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Feedback</th>
                <th className="text-right px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => {
                const info = displayName(s.user_id);
                return (
                  <tr key={s.id} className="border-b border-gold/5 hover:bg-secondary/20 transition-colors">
                    <td className="px-6 py-4 font-body text-sm text-foreground">{info.name}</td>
                    <td className="px-6 py-4 font-body text-sm text-muted-foreground">{info.email}</td>
                    <td className="px-6 py-4 font-body text-sm text-muted-foreground">{formatDate(s.submitted_at)}</td>
                    <td className="px-6 py-4 font-body text-xs uppercase tracking-wider">
                      {s.feedback ? (
                        <span className="text-gold inline-flex items-center gap-1">
                          <CheckCircle2 size={14} /> Reviewed
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openSubmission(s)}
                        className="px-4 py-2 border border-gold/30 text-gold font-body text-xs uppercase tracking-[0.15em] hover:bg-gold/10"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground font-body text-sm">
                    No submissions for this lesson yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HomeworkReviewSection;
